import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { apiBasis } from '../../api/client.ts';
import { useGebruikerMutaties, useGebruikers } from '../../api/gebruikers.ts';
import type { Gebruiker, Gebruikersrol, Gebruikersstatus } from '../../api/typen.ts';
import { useSessie } from '../../auth/SessieContext.tsx';
import { BevestigDialoog } from '../../componenten/BevestigDialoog.tsx';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { useMeldingen } from '../../componenten/Meldingen.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { StatusChip } from '../../componenten/StatusChip.tsx';
import { Keuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { datum, datumTijd } from '../../helpers/formatteer.ts';
import { gebruikersrolLabels, opties } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';
import { useZoekparameters } from '../../hooks/useZoekparameters.ts';

interface Uitnodiging {
  gebruiker: Gebruiker;
  token: string;
  verlooptOp: string;
  aanmeldUrls: { provider: string; naam: string; url: string }[];
}

const rolOpties = opties(gebruikersrolLabels);

function GebruikerFormulier({
  open,
  onSluit,
  bestaand,
  onGemaakt,
}: {
  open: boolean;
  onSluit: () => void;
  bestaand?: Gebruiker | undefined;
  onGemaakt?: ((gebruiker: Gebruiker) => void) | undefined;
}) {
  const mutaties = useGebruikerMutaties();
  const begin = () => ({
    naam: bestaand?.naam ?? '',
    email: bestaand?.email ?? '',
    rol: (bestaand?.rol ?? 'recruiter') as Gebruikersrol,
  });
  const formulier = useForm({ defaultValues: begin() });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, formulier]);
  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      if (bestaand) {
        await mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer: waarden });
      } else {
        onGemaakt?.(await mutaties.maak.mutateAsync(waarden));
      }
    },
    { melding: bestaand ? 'Gebruiker gewijzigd' : 'Gebruiker aangemaakt', naSucces: onSluit },
  );
  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Gebruiker wijzigen' : 'Nieuwe gebruiker'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Tekstveld naam="naam" label="Naam" verplicht autoFocus />
          <Tekstveld naam="email" label="E-mailadres" type="email" verplicht />
          <Keuzeveld naam="rol" label="Rol" opties={rolOpties} verplicht />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

function Kopieerveld({ label, waarde }: { label: string; waarde: string }) {
  const { meld } = useMeldingen();
  return (
    <TextField
      label={label}
      value={waarde}
      fullWidth
      size="small"
      slotProps={{
        htmlInput: { readOnly: true },
        input: {
          endAdornment: (
            <Tooltip title="Kopiëren">
              <IconButton
                size="small"
                aria-label="Kopiëren"
                onClick={() => {
                  navigator.clipboard
                    .writeText(waarde)
                    .then(() => meld('Gekopieerd'))
                    .catch(() => meld('Kopiëren is niet gelukt', 'error'));
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      }}
    />
  );
}

function UitnodigingDialoog({ uitnodiging, onSluit }: { uitnodiging: Uitnodiging; onSluit: () => void }) {
  const basis = apiBasis || window.location.origin;
  const frontendLink = `${window.location.origin}/uitnodiging/${encodeURIComponent(uitnodiging.token)}`;
  return (
    <Dialog open onClose={onSluit} maxWidth="sm" fullWidth>
      <DialogTitle>Uitnodiging voor {uitnodiging.gebruiker.naam}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Stuur een van deze links naar {uitnodiging.gebruiker.email}. De uitnodiging is eenmalig en geldig tot{' '}
            {datumTijd(uitnodiging.verlooptOp)}. Het token is alleen nu zichtbaar.
          </Typography>
          <Kopieerveld label="Uitnodigingspagina in het CRM" waarde={frontendLink} />
          {uitnodiging.aanmeldUrls.map((adres) => (
            <Kopieerveld
              key={adres.provider}
              label={`Direct aanmelden met ${adres.naam}`}
              waarde={new URL(adres.url, basis).href}
            />
          ))}
          {uitnodiging.aanmeldUrls.length === 0 && (
            <Typography variant="body2" color="warning.main">
              Er is nog geen aanmeldmethode ingeschakeld op de server.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSluit} variant="contained">
          Sluiten
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const statussen: Gebruikersstatus[] = ['actief', 'in_afwachting', 'gedeactiveerd'];

/**
 * Het beheer van gebruikers: aanmaken, wijzigen, goedkeuren, deactiveren en uitnodigen. Alleen voor beheerders.
 * @returns De pagina.
 */
export function GebruikersPagina() {
  const { ik } = useSessie();
  const { krijg, zet } = useZoekparameters();
  const status = (statussen as readonly string[]).includes(krijg('status'))
    ? (krijg('status') as Gebruikersstatus)
    : 'actief';
  const gebruikers = useGebruikers(status);
  const mutaties = useGebruikerMutaties();
  const { meld, meldFout } = useMeldingen();
  const [formulier, setFormulier] = useState<{ open: boolean; bestaand?: Gebruiker }>({ open: false });
  const [uitnodiging, setUitnodiging] = useState<Uitnodiging | null>(null);
  const [deactiveren, setDeactiveren] = useState<Gebruiker | null>(null);

  const nodigUit = (gebruiker: Gebruiker) => {
    mutaties.nodigUit
      .mutateAsync(gebruiker.id)
      .then((resultaat) => setUitnodiging({ gebruiker, ...resultaat }))
      .catch(meldFout);
  };

  return (
    <Pagina
      titel="Gebruikers"
      acties={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormulier({ open: true })}>
          Nieuwe gebruiker
        </Button>
      }
    >
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={status}
          onChange={(_gebeurtenis, waarde: Gebruikersstatus) =>
            zet({ status: waarde === 'actief' ? undefined : waarde })
          }
        >
          <Tab value="actief" label="Actief" />
          <Tab value="in_afwachting" label="Wacht op goedkeuring" />
          <Tab value="gedeactiveerd" label="Gedeactiveerd" />
        </Tabs>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Laadstatus query={gebruikers} leeg="Geen gebruikers met deze status.">
          {(lijst) => (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Naam</TableCell>
                    <TableCell>E-mailadres</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Aangemaakt</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lijst.map((gebruiker) => (
                    <TableRow key={gebruiker.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{gebruiker.naam}</TableCell>
                      <TableCell>{gebruiker.email}</TableCell>
                      <TableCell>{gebruikersrolLabels[gebruiker.rol]}</TableCell>
                      <TableCell>
                        <StatusChip soort="gebruiker" waarde={gebruiker.status} />
                      </TableCell>
                      <TableCell>{datum(gebruiker.aangemaaktOp)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {gebruiker.status === 'actief' ? (
                          <>
                            <Tooltip title="Uitnodigen om aan te melden">
                              <IconButton size="small" aria-label="Uitnodigen" onClick={() => nodigUit(gebruiker)}>
                                <MailOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              aria-label="Bewerken"
                              onClick={() => setFormulier({ open: true, bestaand: gebruiker })}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <Button
                              size="small"
                              color="warning"
                              disabled={gebruiker.id === ik?.gebruiker.id}
                              onClick={() => setDeactiveren(gebruiker)}
                            >
                              Deactiveren
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            loading={mutaties.activeer.isPending}
                            onClick={() =>
                              mutaties.activeer
                                .mutateAsync(gebruiker.id)
                                .then(() =>
                                  meld(
                                    gebruiker.status === 'in_afwachting'
                                      ? 'Account goedgekeurd'
                                      : 'Gebruiker geactiveerd',
                                  ),
                                )
                                .catch(meldFout)
                            }
                          >
                            {gebruiker.status === 'in_afwachting' ? 'Goedkeuren' : 'Activeren'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Laadstatus>
      </Paper>
      <GebruikerFormulier
        open={formulier.open}
        onSluit={() => setFormulier({ open: false })}
        bestaand={formulier.bestaand}
        onGemaakt={nodigUit}
      />
      {uitnodiging && <UitnodigingDialoog uitnodiging={uitnodiging} onSluit={() => setUitnodiging(null)} />}
      <BevestigDialoog
        open={deactiveren !== null}
        titel="Gebruiker deactiveren"
        bevestigLabel="Deactiveren"
        gevaarlijk
        bezig={mutaties.deactiveer.isPending}
        onSluit={() => setDeactiveren(null)}
        onBevestig={() => {
          if (deactiveren) {
            mutaties.deactiveer
              .mutateAsync(deactiveren.id)
              .then(() => {
                meld('Gebruiker gedeactiveerd');
                setDeactiveren(null);
              })
              .catch(meldFout);
          }
        }}
      >
        {deactiveren?.naam} kan daarna niet meer inloggen; de geschiedenis blijft bewaard.
      </BevestigDialoog>
    </Pagina>
  );
}
